import React, { useState, useCallback, useRef, useLayoutEffect } from "react";
import { Box, Grid2 } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/**
 * Универсальный контейнер с прокруткой и градиентами сверху/снизу.
 *
 * @param {React.ReactNode} children - содержимое, которое будет скроллиться
 * @param {string|number} height - высота контейнера (по умолчанию "70vh")
 * @param {string} gradientColor - цвет градиента (по умолчанию светло-серый)
 * @param {string|number} gradientHeight - высота градиента (по умолчанию 150px)
 * @param {object} sx - дополнительные стили MUI для внешнего контейнера
 */
const ScrollableWithGradients = ({
    children, height = 600
}) => {
    const scrollRef = useRef(null);
    const contentRef = useRef(null);
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);
    const [containerHeight, setContainerHeight] = useState(height);

    const handleWheel = (e) => {
        e.preventDefault();
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop += e.deltaY / 4; // уменьшили шаг прокрутки в 4 раза
    };

    const updateGradients = useCallback(() => {
        const el = scrollRef.current;
        const content = contentRef.current;
        if (!el) return;
        const { scrollTop, clientHeight } = el;
        const contentHeight = content.scrollHeight;
        const nextHeight = Math.min(contentHeight, height);
        
        setContainerHeight(nextHeight);
        
        const hasScroll = contentHeight > clientHeight + 1;
        
        setShowTopGradient(hasScroll && scrollTop > 0);
        setShowBottomGradient(hasScroll && scrollTop + clientHeight < contentHeight);
    }, [height]);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateGradients(); // сразу после рендера контента
        el.addEventListener("scroll", updateGradients);
        el.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            el.removeEventListener("scroll", updateGradients);
            el.removeEventListener("wheel", handleWheel);
        };
    }, [children, updateGradients]);

    return (
        <Box
          sx={{
            position: "relative",
            height: containerHeight,
            maxHeight: height,
            transition: "height 0.2s ease"
          }}
        >
            {/* Скроллируемый контент */}
            <Box
                ref={scrollRef}
                sx={{
                    maxHeight: "100%",
                    overflow: "auto",
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                }}
            >
                <Grid2
                    ref={contentRef}
                    container
                    spacing={2}
                    sx={{ pt: 3, pb: 3 }}
                >
                    {children}
                </Grid2>
            </Box>

            {/* Верхний градиент */}
            {showTopGradient && (
                <Box
                    sx={{
                        pointerEvents: "none",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 100,
                        background:
                            "linear-gradient(to bottom, rgba(245,245,245,1), rgba(255,255,255,0))",
                    }}
                />
            )}

            {/* Нижний градиент */}
            {showBottomGradient && (
                <Box
                    sx={{
                        pointerEvents: "none",
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 100,
                        background:
                            "linear-gradient(to top, rgba(245,245,245,1), rgba(255,255,255,0))",
                    }}
                />
            )}

            {showTopGradient && (
                <Box
                    sx={{
                        pointerEvents: "none",
                        position: "absolute",
                        top: -25,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 3,
                        opacity: 0.7,
                    }}
                >
                    <ArrowDropUpIcon sx={{ fontSize: 96 }} />
                </Box>
            )}

            {showBottomGradient && (
                <Box
                    sx={{
                        pointerEvents: "none",
                        position: "absolute",
                        bottom: -30,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 3,
                        opacity: 0.7,
                    }}
                >
                    <ArrowDropDownIcon sx={{ fontSize: 96 }} />
                </Box>
            )}

        </Box>
    );
};

export default ScrollableWithGradients;
